import { useState } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import type { ArtworkResponse } from '../../services/artworkService';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';

const Card = styled(motion.button)`
  display: grid;
  grid-template-rows: auto auto;
  width: 100%;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  color: inherit;
  text-align: left;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows.gallery.artwork.subtle};
  transition: transform ${({ theme }) => theme.transitions.gallery.hover}, border-color ${({ theme }) => theme.transitions.gallery.hover}, box-shadow ${({ theme }) => theme.transitions.gallery.hover};

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.gallery.accent.border};
    box-shadow: ${({ theme }) => theme.shadows.gallery.artwork.museum};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 3px;
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const ImageFrame = styled.div<{ $aspectRatio?: number }>`
  position: relative;
  min-height: 0;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || 4 / 5};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: clamp(0.5rem, 1.6vw, 0.875rem);
  background: ${({ theme }) => theme.colors.gallery.mat};
`;

const ArtworkImage = styled(ArtworkObjectImage)`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  opacity: 1;
`;

const ImagePlaceholder = styled.div<{ $error?: boolean }>`
  position: absolute;
  inset: 0.5rem;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 0.35rem;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.gallery.subtle};
  background: ${({ theme, $error }) => $error ? `${theme.colors.error}16` : theme.colors.gallery.surface};
  color: ${({ theme, $error }) => $error ? theme.colors.error : theme.colors.gallery.text.muted};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
  text-align: center;
`;

const Metadata = styled.div`
  display: grid;
  gap: 0.2rem;
  min-width: 0;
  max-height: 7.25rem;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
`;

const StudentName = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.medium};
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AddedDate = styled.span`
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.caption};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
  line-height: 1.3;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  min-width: 0;
`;

const Tag = styled.span`
  max-width: 100%;
  overflow: hidden;
  padding: 0.15rem 0.4rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.gallery.surface};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ViewHint = styled.span`
  color: ${({ theme }) => theme.colors.gallery.text.accent};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
  line-height: 1.3;
  opacity: 0;

  ${Card}:hover &,
  ${Card}:focus-visible & {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
  }
`;

interface EnhancedArtworkCardProps {
  artwork: ArtworkResponse;
  onClick?: (artwork: ArtworkResponse, activatingElement?: HTMLButtonElement) => void;
  aspectRatio?: number;
  priority?: boolean;
  className?: string;
}

function formatDate(dateString?: string): string | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function EnhancedArtworkCard({
  artwork,
  onClick,
  aspectRatio,
  priority = false,
  className,
}: EnhancedArtworkCardProps) {
  const shouldReduceMotion = useReducedMotion();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const studentName = artwork.student_name?.trim() || 'Student artwork';
  const date = formatDate(artwork.uploaded_at);
  const tagNames = (artwork.tags || []).map((tag: unknown) =>
    typeof tag === 'string' ? tag : (tag as { tag_name?: string })?.tag_name,
  ).filter((tag): tag is string => Boolean(tag));

  const handleImageError = () => {
    setHasImageError(true);
    setIsImageLoaded(false);
  };

  return (
    <Card
      type="button"
      className={className}
      onClick={(event) => onClick?.(artwork, event.currentTarget)}
      aria-label={`View artwork by ${studentName}`}
      data-artwork-id={artwork.artwork_id}
      data-testid="artwork-card"
      initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.98 }}
      animate={shouldReduceMotion ? undefined : { opacity: 1, scale: 1 }}
      transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.25 }}
      whileHover={shouldReduceMotion ? undefined : { y: -3 }}
    >
      <ImageFrame $aspectRatio={aspectRatio}>
        {!isImageLoaded && (
          <ImagePlaceholder $error={hasImageError}>
            <span aria-hidden="true">{hasImageError ? '▧' : '…'}</span>
            <span>{hasImageError ? 'Image unavailable' : 'Loading image'}</span>
          </ImagePlaceholder>
        )}
        {!hasImageError && (
          <ArtworkImage
            artworkId={artwork.artwork_id}
            variant="thumbnail"
            updatedAt={artwork.updated_at}
            access="public"
            lazy={!priority}
            alt={`Artwork by ${studentName}`}
            loading={priority ? 'eager' : 'lazy'}
            onLoad={() => setIsImageLoaded(true)}
            onError={handleImageError}
            onLoadError={handleImageError}
          />
        )}
      </ImageFrame>
      <Metadata>
        <StudentName title={studentName}>{studentName}</StudentName>
        {date && <AddedDate><span>Added </span><span>{date}</span></AddedDate>}
        {tagNames.length > 0 && (
          <Tags aria-label="Artwork tags">
            {tagNames.slice(0, 3).map((tag) => <Tag key={tag}>{tag}</Tag>)}
            {tagNames.length > 3 && <Tag>+{tagNames.length - 3} more</Tag>}
          </Tags>
        )}
        <ViewHint>View artwork</ViewHint>
      </Metadata>
    </Card>
  );
}
